import { AxiosResponse } from 'axios'
import qs from 'qs'

import { Metable } from '../../types/metable'
import { getRequest } from '../../utils/api'
import apiZervant from '../../utils/apiZervant'
import { DownloadDocumentFileType } from './enums/downloadDocumentFileType'
import { InvoiceDocumentType } from './enums/invoiceDocumentType'
import { InvoiceStatus } from './enums/invoiceStatus'
import { QuoteStatus } from './enums/quoteStatus'
import { ZervantLocale } from './enums/zervantLocale'
import { ExternalInvoiceFromBilly } from './types/externalInvoiceFromBilly'
import { FetchInvoicesSummaryParams } from './types/fetchInvoicesSummaryParams'
import { InvoicePayload } from './types/invoicePayload/invoicePayload'
import { Invoice } from './types/invoices/invoice'
import { InvoiceEmailTemplateDocumentType } from './types/invoices/invoinceEmailTemplateDocumentType'
import type { InvoicesSummaryData } from './types/invoicesSummaryData'
import type { Quote } from './types/quotes/quote'
import type { QuotesSummaryData } from './types/quotes/quotesSummaryData'
import { FetchSalesDocumentsParams } from './types/salesDocument/fetchSalesDocumentsParams'
import { FetchSalesDocumentsSummaryParams } from './types/salesDocument/fetchSalesDocumentsSummaryParams'
import { SalesDocumentPropsByListingType } from './types/salesDocument/salesDocumentPropsByListingType'
import { SalesListingDocumentType } from './types/salesDocument/salesListingDocumentType'
import { getInvoicingApiCategory } from './utils/getInvoicingApiCategory'
import { getSalesQueryDocumentTypes } from './utils/getSalesQueryDocumentTypes'

// Invoices

export type FetchDocument = (id: string) => Promise<{ data: Invoice }>
export type FetchDocumentData = Awaited<ReturnType<FetchDocument>>
export type FetchInvoiceResponseData = Invoice
export type FetchCreditNoteResponseData = FetchInvoiceResponseData

export const fetchInvoice: FetchDocument = async (invoiceId) => {
  return apiZervant.restInvoice.get<FetchInvoiceResponseData>(`/v1/invoices/${invoiceId}`)
}

export const fetchCreditNote: FetchDocument = async (invoiceId) => {
  return apiZervant.restInvoice.get<FetchCreditNoteResponseData>(`/v1/creditnotes/${invoiceId}`)
}

// Sales Document Drafts

export interface DeleteSalesDocumentDraftOptions {
  documentId: number
  documentType: InvoiceDocumentType
}

export const deleteSalesDocumentDraft = ({ documentId, documentType }: DeleteSalesDocumentDraftOptions) => {
  const apiCategory = getInvoicingApiCategory(documentType)
  return apiZervant.restInvoice.delete(`/drafts/${apiCategory}/${documentId}`)
}

// Sales Documents

type SalesListingDocumentTypeToDocumentList = {
  [K in SalesListingDocumentType]: SalesDocumentPropsByListingType[K]['salesDocumentType'][]
}

export interface FetchSalesDocumentsResponseData<T extends SalesListingDocumentType> extends Metable {
  documents: SalesListingDocumentTypeToDocumentList[T]
}

export const fetchSalesDocuments = async <T extends SalesListingDocumentType>({
  customerId,
  documentTypes: documentTypesOverride,
  documentsListType,
  documentNumber,
  fromDate,
  page,
  pageSize,
  searchQuery: searchText,
  sortDirection,
  sortProperty,
  status,
  toDate,
}: FetchSalesDocumentsParams<T>) => {
  const documentTypes = documentTypesOverride?.length
    ? documentTypesOverride
    : getSalesQueryDocumentTypes(documentsListType)

  const queryParams = qs.stringify({
    ...(customerId ? { customerId } : {}),
    ...(documentNumber ? { documentNumber } : {}),
    ...(documentTypes?.length ? { documentType: documentTypes.join(',') } : {}),
    ...(fromDate && toDate ? { fromDate, toDate } : {}),
    ...(page ? { page } : {}),
    ...(pageSize ? { pageSize } : {}),
    ...(searchText ? { searchText } : {}),
    ...(sortDirection ? { sortDirection } : {}),
    ...(sortProperty ? { sortProperty } : {}),
    ...(status ? { status } : {}),
  })

  return apiZervant.restInvoice.get<FetchSalesDocumentsResponseData<T>>(`/v1/documents/all?${queryParams}`)
}

// Invoice Preview

interface PreviewInvoiceDraftOptions {
  documentType: InvoiceDocumentType
  payload: InvoicePayload
}

// Invoice preview's response is an array, while credit note preview's response is a string
type PreviewInvoiceDraftEndpointResponse = string[] | string
export type PreviewInvoiceDraftResponse = string[]

export const previewInvoiceDraft = async ({
  documentType,
  payload,
}: PreviewInvoiceDraftOptions): Promise<AxiosResponse<PreviewInvoiceDraftResponse>> => {
  const apiCategory = getInvoicingApiCategory(documentType)

  const response = await apiZervant.restInvoice.post<PreviewInvoiceDraftEndpointResponse>(
    `/drafts/${apiCategory}/preview/html/live`,
    payload,
  )

  // Invoice preview's response is an array, while credit note preview's response
  // is a string, so we ensure the returned format consistency
  return { ...response, data: Array.isArray(response.data) ? response.data : [response.data] }
}

// Document PDF Download

export interface FetchInvoiceDownloadDataOptions {
  downloadDocumentFileType: DownloadDocumentFileType
  documentType: InvoiceDocumentType
  invoiceId: string
}

export interface FetchInvoiceDownloadDataResponse {
  data: Blob
}

export const fetchInvoiceDownloadData = async ({
  downloadDocumentFileType,
  documentType,
  invoiceId,
}: FetchInvoiceDownloadDataOptions): Promise<FetchInvoiceDownloadDataResponse> => {
  const fileType = downloadDocumentFileType === DownloadDocumentFileType.XML ? 'xrechnung' : 'pdf'
  const apiCategory = getInvoicingApiCategory(documentType)
  const versionPrefix = documentType === InvoiceDocumentType.DeliveryNote ? 'v1/' : ''

  return apiZervant.restInvoice.get(`/${versionPrefix}${apiCategory}/${invoiceId}/${fileType}`, {
    responseType: 'blob',
  })
}

// invoiceDelivery requests

interface FetchInvoiceEmailTemplateResponse {
  data: { body: string; isDefault: boolean }
}

export const fetchInvoiceEmailTemplate = async (
  documentType: InvoiceEmailTemplateDocumentType,
  invoiceLocale: ZervantLocale,
): Promise<FetchInvoiceEmailTemplateResponse> => {
  return apiZervant.invoiceDelivery.get(`/email/template/${documentType}/${invoiceLocale}`)
}

export interface SendInvoiceAsEmailPayload {
  acceptStripePayment: boolean
  attachmentUuids?: string[]
  bcc?: string
  body?: string
  captchaToken: string
  subject: string
  to: string
}

export interface SendInvoiceAsEmailOptions {
  documentType: InvoiceDocumentType
  invoiceId: number
  payload: SendInvoiceAsEmailPayload
}

export interface SendAsEmailResponse {
  isSent: boolean
}

export const sendInvoiceAsEmail = ({ documentType, invoiceId, payload }: SendInvoiceAsEmailOptions) => {
  return apiZervant.invoiceDelivery.post<SendAsEmailResponse>(`/email/${documentType}/${invoiceId}`, payload)
}

// Quotes

export type FetchQuoteResponseData = Quote

export const deleteQuote = async (quoteId: number) => {
  await apiZervant.restInvoice.post(`/estimates/${quoteId}/deleteevent`)
}

export const deleteInvoice = async (invoiceId: number) => {
  await apiZervant.restInvoice.delete(`/invoices/${invoiceId}`)
}

export interface UpdateQuoteStatusOptions {
  quoteId: string
  value: QuoteStatus
}

export const updateQuoteStatus = async ({ quoteId, value }: UpdateQuoteStatusOptions) => {
  await apiZervant.restInvoice.post(`/estimates/${quoteId}/event/${value}`)
}

type SendPaymentReminderOptions = SendInvoiceAsEmailOptions

export const sendPaymentReminder = ({ documentType, invoiceId, payload }: SendPaymentReminderOptions) => {
  return apiZervant.invoiceDelivery.post<SendAsEmailResponse>(
    `/reminders/v1/${documentType}/${invoiceId}/email`,
    payload,
  )
}

interface SendAsEInvoicePayload {
  attachmentUuids?: string[]
  isPublicEntity: boolean
}

interface SendAsEInvoiceOptions {
  documentType: InvoiceDocumentType
  invoiceId: number
  payload: SendAsEInvoicePayload
}

interface SendAsEInvoiceViaFrenchPdpPayload {
  addressingLine?: string
}

interface SendAsEInvoiceViaFrenchPdpOptions {
  documentType: InvoiceDocumentType
  invoiceId: number
  payload: SendAsEInvoiceViaFrenchPdpPayload
}

interface SendAsEInvoiceResponse {
  bumId?: string
}

// Non-French company's e-invoice gets sent with Basware
export const sendAsEInvoice = ({ documentType, invoiceId, payload }: SendAsEInvoiceOptions) => {
  return apiZervant.invoiceDelivery.post<SendAsEInvoiceResponse>(`/v3/einvoice/${documentType}/${invoiceId}`, payload)
}

// French only mandate-enrolled users → backend routes to Cegid PA or Shine PA (Serensia) depending on isCegid value
export const sendAsEInvoiceViaFrenchPdp = ({ documentType, invoiceId, payload }: SendAsEInvoiceViaFrenchPdpOptions) => {
  return apiZervant.invoiceDelivery.post<SendAsEInvoiceResponse>(
    `/inhouse/einvoice/${documentType}/${invoiceId}`,
    payload,
  )
}

interface DeleteAttachmentOptions {
  sessionId: string
  uuid: string
}

export const deleteAttachment = ({ sessionId, uuid }: DeleteAttachmentOptions) => {
  return apiZervant.invoiceDelivery.delete(`/attachment/${sessionId}/${uuid}`)
}

interface FetchInvoicesSummaryResponseData extends Metable {
  summary: InvoicesSummaryData
}

export const fetchInvoicesSummary = ({
  customerId,
  fromDate,
  searchQuery: searchText,
  toDate,
}: FetchInvoicesSummaryParams) => {
  const queryParams = qs.stringify({
    ...(customerId ? { customerId } : {}),
    ...(searchText ? { searchText } : {}),
    ...(fromDate && toDate ? { fromDate, toDate } : {}),
  })

  return apiZervant.restInvoice.get<FetchInvoicesSummaryResponseData>(`/v1/documents/invoice-summary?${queryParams}`)
}

interface FetchQuotesSummaryResponseData extends Metable {
  summary: QuotesSummaryData
}

export const fetchQuotesSummary = ({
  customerId,
  searchQuery: searchText,
  fromDate,
  toDate,
}: FetchSalesDocumentsSummaryParams) => {
  const queryParams = qs.stringify({
    ...(customerId ? { customerId } : {}),
    ...(searchText ? { searchText } : {}),
    ...(fromDate && toDate ? { fromDate, toDate } : {}),
  })

  return apiZervant.restInvoice.get<FetchQuotesSummaryResponseData>(`/v1/documents/estimate-summary?${queryParams}`)
}

export interface UpdateApprovedQuoteOptions {
  payload: Quote
  quoteId: string
}

export const updateApprovedQuote = async ({ payload, quoteId }: UpdateApprovedQuoteOptions) => {
  return apiZervant.restInvoice.put<Quote>(`/v1/estimates/${quoteId}`, payload)
}

// Billy API

interface FetchExternalInvoiceResponseData extends Metable {
  externalInvoice: ExternalInvoiceFromBilly
}

export const fetchExternalInvoice = (externalInvoiceId: string): Promise<FetchExternalInvoiceResponseData> =>
  getRequest(`/v2/externalInvoices/${externalInvoiceId}?include=externalInvoice.balanceModifiers:embed`)

// Invoice Status

export type InvoiceStatusPayload = { status: InvoiceStatus }

interface SetInvoiceStatusOptions {
  documentType: InvoiceDocumentType
  invoiceId: number
  payload: InvoiceStatusPayload
}

export type SetInvoiceResponseData = Invoice

export const setInvoiceStatus = async ({ documentType, invoiceId, payload }: SetInvoiceStatusOptions) => {
  const apiCategory = getInvoicingApiCategory(documentType)
  const response = await apiZervant.restInvoice.post<SetInvoiceResponseData>(
    `/${apiCategory}/${invoiceId}/status`,
    payload,
  )

  return response.data
}
